import isHLSVideo from 'helpers/is/isHLSVideo'
import { RefObject, useEffect } from 'react'
import type Hls from 'hls.js'
import { logger } from 'helpers/logger'

const hlsConfig = {
  maxBufferLength: 10,
}

type HookProps = {
  videoRef: RefObject<HTMLVideoElement>
  videoSrc: string
  onInit: () => void
}
const loadHls = async (): Promise<typeof Hls> => {
  const mod: { default: typeof Hls } = await import('hls.js')
  return mod.default
}

const useHLSVideo = ({ videoRef, videoSrc, onInit }: HookProps) => {
  const video = videoRef.current
  const isHLS = isHLSVideo(videoSrc)

  useEffect(() => {
    if (!isHLS || !video) return

    try {
      const setup = async () => {
        const HLS: typeof Hls = await loadHls()

        const isNativeSupport = !!video.canPlayType(
          'application/vnd.apple.mpegurl',
        )

        if (isNativeSupport) {
          video.src = videoSrc
          return
        }

        if (HLS.isSupported()) {
          const hls: Hls = new HLS(hlsConfig)
          hls.loadSource(videoSrc)
          hls.attachMedia(video)
          hls.on(HLS.Events.MANIFEST_LOADED, onInit)
        } else {
          logger.error(`HLS.js is not supported in this browser`)
        }
      }

      void setup()
    } catch (error) {
      logger.error('Error loading HLS', error)
    }
  }, [isHLS, video, onInit])
}

export default useHLSVideo
